import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import Section from "@/pages/settings/components/sections/Section";
import {
  useFormProductMap,
  useSaveFormProductMap
} from "@/entities/formProductMap/hooks";
import { FormProductMap } from "@/entities/formProductMap/types";
import { useProducts } from "@/entities/settings/hooks";
import { FormProductMapDialog } from "@/pages/settings/components/integrations/hubspot/FormProductMapDialog";
import {
  HUBSPOT_SETTINGS_TRANSLATION_PREFIX,
  formatProducts
} from "@/pages/settings/components/integrations/hubspot/hubspotSettingsOptions";

const PREFIX = `${HUBSPOT_SETTINGS_TRANSLATION_PREFIX}.productMap`;

// The account's form product map, shared with the website form intake, so it
// saves on its own rather than with the HubSpot settings.
export const FormProductMapSection: React.FC = () => {
  const { t } = useTranslation();
  const mapQuery = useFormProductMap();
  const productsQuery = useProducts();
  const save = useSaveFormProductMap();
  const [isEditing, setIsEditing] = useState(false);

  const productMap = mapQuery.data;
  const products = productsQuery.data?.products ?? [];
  // Without the catalog, no rule's product can be told apart from a removed one.
  const isCatalogUnavailable = productsQuery.isLoading || productsQuery.isError;
  const productName = (productId: string) =>
    products.find((product) => product.id === productId)?.name ??
    (isCatalogUnavailable ? "" : t(`${PREFIX}.notInCatalog`));
  const subTitle = mapQuery.isError
    ? t(`${PREFIX}.loadError`)
    : productsQuery.isError
      ? t(`${PREFIX}.productsLoadError`)
      : t(`${PREFIX}.subtitle`);

  const saveMap = (edited: FormProductMap) =>
    save.mutate(
      { data: edited },
      {
        onSuccess: () => setIsEditing(false),
        onError: () => toast.error(t(`${PREFIX}.updateError`))
      }
    );

  return (
    <>
      <Section
        title={t(`${PREFIX}.title`)}
        subTitle={subTitle}
        isLoading={mapQuery.isLoading}
        action={
          <Button
            variant="secondary"
            data-test="form-product-map-edit"
            onClick={() => setIsEditing(true)}
            disabled={!productMap || save.isPending}
          >
            {t(`${PREFIX}.edit`)}
          </Button>
        }
        items={(productMap?.rules ?? []).map((rule) => ({
          title: formatProducts(rule.values),
          subTitle: productName(rule.productId)
        }))}
      >
        {productMap && productMap.rules.length === 0 && (
          <Text variant="bodySecondary">{t(`${PREFIX}.empty`)}</Text>
        )}
      </Section>
      {isEditing && productMap && (
        <FormProductMapDialog
          productMap={productMap}
          products={products}
          isCatalogUnavailable={isCatalogUnavailable}
          isSaving={save.isPending}
          onSave={saveMap}
          onClose={() => setIsEditing(false)}
        />
      )}
    </>
  );
};
